import { FlexibleConnectedPositionStrategy, Overlay, OverlayRef, ScrollDispatcher } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';

import { Subject } from 'rxjs';
import { HxBaseControl } from '../../base/hx-base-control';

export type TooltipPosition =
  'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';

export type TooltipColor = 'info' | 'success' | 'warning' | 'danger' | 'neutral';
export type TooltipContent = string | TemplateRef<unknown>;

/**
 * Internal component used to render tooltip content within CDK Overlay portal. This component is instantiated by
 * HxTooltipDirective and should not be used directly.
 *
 * @internal
 * @component HxTooltip
 */
@Component({
  selector: 'hx-tooltip',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tooltip.html',
  styleUrls: ['./tooltip.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class HxTooltip extends HxBaseControl {
  /** Tooltip content text */
  readonly content = input<string>('');

  /** Optional template content for rich tooltip rendering */
  readonly contentTemplate = input<TemplateRef<unknown> | null>(null);

  /** Optional context passed to template content */
  readonly contentTemplateContext = input<Record<string, unknown> | null>(null);

  /** Position variant for arrow alignment */
  readonly position = input<TooltipPosition>('top');

  /** Color scheme variant */
  readonly variant = input<TooltipColor>('info');

  /** Returns CSS class for arrow positioning based on position input Maps position variants to arrow direction classes */
  get arrowClass(): string {
    const positionMap: Record<TooltipPosition, string> = {
      top: 'hx-tooltip__arrow--top',
      bottom: 'hx-tooltip__arrow--bottom',
      left: 'hx-tooltip__arrow--left',
      right: 'hx-tooltip__arrow--right',
      'top-start': 'hx-tooltip__arrow--top',
      'top-end': 'hx-tooltip__arrow--top',
      'bottom-start': 'hx-tooltip__arrow--bottom',
      'bottom-end': 'hx-tooltip__arrow--bottom',
    };
    return positionMap[this.position()];
  }
}

/**
 * HxTooltip Directive - Attribute directive providing contextual help text with hover/focus triggers
 *
 * Applies to any host element via [hxTooltip] attribute. Renders tooltip in CDK Overlay with intelligent positioning,
 * keyboard navigation, and ARIA accessibility support.
 *
 * Based on Angular Material's MatTooltip implementation with enhanced positioning and event handling.
 *
 * @example
 *   ```html
 *   <!-- Basic usage -->
 *   <button hxTooltip="Click to submit">Submit</button>
 *
 *   <!-- Custom delays -->
 *   <button hxTooltip="Help text" [showDelay]="500" [hideDelay]="200">Help</button>
 *
 *   <!-- All position variants -->
 *   <button hxTooltip="Help text" position="top-start">Help</button>
 *   <button hxTooltip="Help text" position="bottom">Help</button>
 *   ```;
 *
 * @directive HxTooltipDirective
 */
@Directive({
  selector: '[hxTooltip]',
  standalone: true,
  host: {
    '[class.hx-tooltip-trigger]': 'true',
    '[class.hx-tooltip-disabled]': 'disabled()',
  },
})
export class HxTooltipDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly overlay = inject(Overlay);
  private readonly renderer2 = inject(Renderer2);
  private readonly ngZone = inject(NgZone);
  private readonly scrollDispatcher = inject(ScrollDispatcher);
  private readonly document = inject(DOCUMENT);

  // ============================================================================
  // INPUTS
  // ============================================================================

  /** Tooltip content text or template (required) */
  readonly tooltipContent = input.required<TooltipContent>({
    alias: 'hxTooltip',
  });

  /** Optional context object supplied when hxTooltip is a template */
  readonly tooltipContext = input<Record<string, unknown> | null>(null);

  /**
   * Tooltip position relative to host element Default: 'top' Options: top, bottom, left, right, top-start, top-end,
   * bottom-start, bottom-end
   */
  readonly position = input<TooltipPosition>('top');

  /** Color scheme variant */
  readonly tooltipColor = input<TooltipColor>('info');

  /**
   * Delay before tooltip appears on hover (milliseconds) Default: 200ms Focus events bypass this delay (0ms for
   * keyboard users)
   */
  readonly showDelay = input<number>(200);

  /**
   * Delay before tooltip hides after mouseleave (milliseconds) Default: 0ms (immediate hide) Allows brief pause before
   * tooltip closes for smooth interaction
   */
  readonly hideDelay = input<number>(0);

  /** Whether tooltip is disabled (prevents interaction while preserving layout) */
  readonly disabled = input<boolean>(false);

  // ============================================================================
  // INTERNAL STATE
  // ============================================================================

  private overlayRef: OverlayRef | null = null;
  private componentRef: any = null;
  private showTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private hideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private touchstartTimeout: ReturnType<typeof setTimeout> | null = null;
  private tooltipId: string = '';
  private isTooltipVisible = signal(false);
  private pointerExitEventsInitialized = false;
  private eventListeners: Array<() => void> = [];
  private readonly destroyed$ = new Subject<void>();
  private readonly MIN_VIEWPORT_TOOLTIP_THRESHOLD = 8;
  private readonly UNBOUNDED_ANCHOR_GAP = 8;

  constructor() {
    // Keep an already-open tooltip's text/template live (e.g. status labels that keep changing while
    // hovered) instead of only snapshotting tooltipContent() once at show() time.
    effect(() => {
      const content = this.tooltipContent();
      const context = this.tooltipContext();

      if (!this.componentRef) {
        return;
      }

      if (typeof content === 'string') {
        this.componentRef.setInput('content', content);
        this.componentRef.setInput('contentTemplate', null);
        this.componentRef.setInput('contentTemplateContext', null);
      } else {
        this.componentRef.setInput('content', '');
        this.componentRef.setInput('contentTemplate', content);
        this.componentRef.setInput('contentTemplateContext', context);
      }
    });
  }

  ngOnInit(): void {
    // Skip setup if tooltip is disabled
    if (this.disabled()) {
      return;
    }

    // Generate unique ID for ARIA linkage
    this.tooltipId = `tooltip-${Math.random().toString(36).substring(2, 11)}`;

    // Setup event listeners on appropriate trigger element
    this.setupPointerEnterEvents();
  }

  /**
   * Public API method to show tooltip (like Material's show())
   *
   * @param delay Optional override for show delay
   */
  show(delay: number = this.showDelay()): void {
    if (this.disabled() || !this.tooltipContent() || this.isTooltipVisible()) {
      return;
    }

    // Clear any existing show timeout
    if (this.showTimeoutId !== null) {
      clearTimeout(this.showTimeoutId);
    }

    // Schedule show with delay
    this.showTimeoutId = this.ngZone.runOutsideAngular(() =>
      setTimeout(() => {
        this.ngZone.run(() => {
          this.createAndAttachTooltip();
          this.showTimeoutId = null;
        });
      }, delay)
    );
  }

  /**
   * Public API method to hide tooltip (like Material's hide())
   *
   * @param delay Optional override for hide delay
   */
  hide(delay: number = this.hideDelay()): void {
    // Cancel pending show
    if (this.showTimeoutId !== null) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }

    if (!this.isTooltipVisible()) {
      return;
    }

    // Clear any existing hide timeout
    if (this.hideTimeoutId !== null) {
      clearTimeout(this.hideTimeoutId);
    }

    // Schedule hide with delay
    this.hideTimeoutId = this.ngZone.runOutsideAngular(() =>
      setTimeout(() => {
        this.ngZone.run(() => {
          this.detachAndCleanupTooltip();
          this.hideTimeoutId = null;
        });
      }, delay)
    );
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  /**
   * Setup pointer enter events (mouseenter for desktop, touchstart for mobile) Follows Material's
   * _setupPointerEnterEventsIfNeeded pattern
   */
  private setupPointerEnterEvents(): void {
    // Listen for mouseenter on desktop
    this.addListener('mouseenter', (event: MouseEvent) => {
      this.setupPointerExitEvents();
      this.show();
    });

    // Listen for touchstart on mobile with long-press detection
    this.addListener('touchstart', (event: TouchEvent) => {
      const touch = event.targetTouches?.[0];
      const origin = touch ? { x: touch.clientX, y: touch.clientY } : undefined;

      this.setupPointerExitEvents();

      // Clear existing touchstart timeout
      if (this.touchstartTimeout) {
        clearTimeout(this.touchstartTimeout);
      }

      // Long-press delay (500ms like Material)
      const LONG_PRESS_DELAY = 500;
      this.touchstartTimeout = this.ngZone.runOutsideAngular(() =>
        setTimeout(() => {
          this.ngZone.run(() => {
            this.show();
          });
        }, LONG_PRESS_DELAY)
      );
    });
  }

  /**
   * Setup pointer exit events (mouseleave for desktop, touchend/touchcancel for mobile) Only initialize once to avoid
   * duplicate listeners
   */
  private setupPointerExitEvents(): void {
    if (this.pointerExitEventsInitialized) {
      return;
    }

    this.pointerExitEventsInitialized = true;

    // Desktop: mouseleave with check for overlay element
    this.addListener('mouseleave', (event: MouseEvent) => {
      const newTarget = event.relatedTarget as Node | null;

      // Check if mouse moved to overlay or stayed within trigger
      if (!newTarget || !this.overlayRef?.overlayElement.contains(newTarget)) {
        this.hide();
      }
    });

    // Mobile: touchend and touchcancel
    const touchendListener = () => {
      // Clear pending long-press
      if (this.touchstartTimeout) {
        clearTimeout(this.touchstartTimeout);
        this.touchstartTimeout = null;
      }
      this.hide();
    };

    this.addListener('touchend', touchendListener);
    this.addListener('touchcancel', touchendListener);

    // Keyboard: Escape key
    this.addListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape' && this.isTooltipVisible()) {
        event.preventDefault();
        event.stopPropagation();
        this.hide(0); // Hide immediately on Escape
      }
    });

    // Focus and blur for keyboard accessibility
    this.addListener('focus', () => {
      this.show(0); // Show immediately on focus (no delay for accessibility)
    });

    this.addListener('blur', () => {
      this.hide();
    });
  }

  /** Add event listener and track for cleanup Follows Material's _addListener pattern with event listener cleanup */
  private addListener(
    event: string,
    listener: (event: any) => void,
    options?: boolean | AddEventListenerOptions
  ): void {
    // For scroll-blocking events (touchstart, touchend, touchcancel, wheel), use passive option for better performance
    const isScrollBlockingEvent = ['touchstart', 'touchend', 'touchcancel', 'wheel', 'mousewheel'].includes(event);

    if (isScrollBlockingEvent) {
      // Use native addEventListener with passive option to prevent violation warnings
      const eventOptions: AddEventListenerOptions =
        typeof options === 'boolean'
          ? { passive: true, capture: options }
          : { passive: true, ...(typeof options === 'object' ? options : {}) };

      this.elementRef.nativeElement.addEventListener(event, listener, eventOptions);

      // Track the listener for cleanup
      const unlisten = () => {
        this.elementRef.nativeElement.removeEventListener(event, listener, eventOptions);
      };
      this.eventListeners.push(unlisten);
    } else {
      // Use Renderer2 for other events (better Angular integration)
      const listenerFn = this.renderer2.listen(this.elementRef.nativeElement, event, listener);
      this.eventListeners.push(listenerFn);
    }
  }

  /** Create and attach tooltip component to overlay Follows Material's show() method pattern */
  private createAndAttachTooltip(): void {
    if (this.isTooltipVisible()) {
      return;
    }

    // Create overlay if needed
    if (!this.overlayRef) {
      this.overlayRef = this.createOverlay();

      // Add mouseleave listener to the overlay element to hide when mouse leaves tooltip
      this.overlayRef.overlayElement.addEventListener('mouseleave', (event: MouseEvent) => {
        const newTarget = event.relatedTarget as Node | null;

        // Check if mouse moved back to trigger element
        if (!newTarget || !this.elementRef.nativeElement.contains(newTarget)) {
          this.hide();
        }
      });
    }

    // Attach component portal
    const portal = new ComponentPortal(HxTooltip);
    this.componentRef = this.overlayRef.attach(portal);

    // Set component inputs
    this.componentRef.setInput('id', this.tooltipId);
    const tooltipContent = this.tooltipContent();
    if (typeof tooltipContent === 'string') {
      this.componentRef.setInput('content', tooltipContent);
      this.componentRef.setInput('contentTemplate', null);
      this.componentRef.setInput('contentTemplateContext', null);
    } else {
      this.componentRef.setInput('content', '');
      this.componentRef.setInput('contentTemplate', tooltipContent);
      this.componentRef.setInput('contentTemplateContext', this.tooltipContext());
    }
    this.componentRef.setInput('position', this.position());
    this.componentRef.setInput('variant', this.tooltipColor());

    // Set ARIA attributes
    this.renderer2.setAttribute(this.elementRef.nativeElement, 'aria-describedby', this.tooltipId);

    // Mark as visible
    this.isTooltipVisible.set(true);

    // Start monitoring position changes
    this.monitorPositionChanges();
  }

  /** Detach and cleanup tooltip */
  private detachAndCleanupTooltip(): void {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
      this.componentRef = null;
    }

    this.renderer2.removeAttribute(this.elementRef.nativeElement, 'aria-describedby');
    this.isTooltipVisible.set(false);
  }

  /**
   * Create CDK Overlay with positioning strategy and scroll handling Similar to Material's _createOverlay method
   *
   * @returns OverlayRef instance
   */
  private createOverlay(): OverlayRef {
    // Get scroll ancestors for auto-repositioning
    const scrollableAncestors = this.scrollDispatcher.getAncestorScrollContainers(this.elementRef);

    // Create positioning strategy
    const positionStrategy = this.buildPositionStrategy();

    // Apply scroll ancestors to strategy
    positionStrategy.withScrollableContainers(scrollableAncestors);

    // Create overlay with scroll strategy that repositions on scroll
    const scrollStrategy = this.overlay.scrollStrategies.reposition();

    return this.overlay.create({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: false,
      panelClass: 'hx-tooltip-overlay',
      direction: (this.document.dir as 'ltr' | 'rtl') || 'ltr', // RTL support
    });
  }

  /**
   * Build CDK FlexibleConnectedPositionStrategy with position variants Follows Material's approach: main position +
   * fallback position
   *
   * @returns Configured FlexibleConnectedPositionStrategy
   */
  private buildPositionStrategy(): FlexibleConnectedPositionStrategy {
    const strategy = this.overlay.position().flexibleConnectedTo(this.elementRef.nativeElement);

    // Get origin and overlay positions (Material pattern)
    const originPos = this._getOrigin();
    const overlayPos = this._getOverlayPosition();

    // Build main and fallback positions with offsets
    const positions = [
      this._addOffset({ ...originPos.main, ...overlayPos.main }),
      this._addOffset({ ...originPos.fallback, ...overlayPos.fallback }),
    ];

    // Apply positions in order (first is preferred, others are fallbacks)
    strategy.withPositions(positions);

    // Enable automatic repositioning for viewport collisions
    strategy.withFlexibleDimensions(false);
    strategy.withPush(true);

    return strategy;
  }

  /**
   * Returns the origin position and a fallback position based on the user's position preference. The fallback position
   * is the inverse of the origin (e.g. 'top' -> 'bottom'). Follows Material's _getOrigin pattern.
   */
  private _getOrigin(): {
    main: { originX: 'start' | 'center' | 'end'; originY: 'top' | 'center' | 'bottom' };
    fallback: { originX: 'start' | 'center' | 'end'; originY: 'top' | 'center' | 'bottom' };
  } {
    const position = this.position();
    let originPosition: { originX: 'start' | 'center' | 'end'; originY: 'top' | 'center' | 'bottom' };

    if (position === 'top' || position === 'top-start' || position === 'top-end') {
      originPosition = { originX: 'center', originY: 'top' };
    } else if (position === 'bottom' || position === 'bottom-start' || position === 'bottom-end') {
      originPosition = { originX: 'center', originY: 'bottom' };
    } else if (position === 'left') {
      originPosition = { originX: 'start', originY: 'center' };
    } else if (position === 'right') {
      originPosition = { originX: 'end', originY: 'center' };
    } else {
      // Default to top
      originPosition = { originX: 'center', originY: 'top' };
    }

    // Adjust for start/end variants
    if (position === 'top-start' || position === 'bottom-start') {
      originPosition.originX = 'start';
    } else if (position === 'top-end' || position === 'bottom-end') {
      originPosition.originX = 'end';
    }

    const { x, y } = this._invertPosition(originPosition.originX, originPosition.originY);
    return {
      main: originPosition,
      fallback: { originX: x, originY: y },
    };
  }

  /**
   * Returns the overlay position and a fallback position based on the user's preference. Follows Material's
   * _getOverlayPosition pattern.
   */
  private _getOverlayPosition(): {
    main: { overlayX: 'start' | 'center' | 'end'; overlayY: 'top' | 'center' | 'bottom' };
    fallback: { overlayX: 'start' | 'center' | 'end'; overlayY: 'top' | 'center' | 'bottom' };
  } {
    const position = this.position();
    let overlayPosition: { overlayX: 'start' | 'center' | 'end'; overlayY: 'top' | 'center' | 'bottom' };

    if (position === 'top' || position === 'top-start' || position === 'top-end') {
      overlayPosition = { overlayX: 'center', overlayY: 'bottom' };
    } else if (position === 'bottom' || position === 'bottom-start' || position === 'bottom-end') {
      overlayPosition = { overlayX: 'center', overlayY: 'top' };
    } else if (position === 'left') {
      overlayPosition = { overlayX: 'end', overlayY: 'center' };
    } else if (position === 'right') {
      overlayPosition = { overlayX: 'start', overlayY: 'center' };
    } else {
      // Default to top
      overlayPosition = { overlayX: 'center', overlayY: 'bottom' };
    }

    // Adjust for start/end variants
    if (position === 'top-start' || position === 'bottom-start') {
      overlayPosition.overlayX = 'start';
    } else if (position === 'top-end' || position === 'bottom-end') {
      overlayPosition.overlayX = 'end';
    }

    const { x, y } = this._invertPosition(overlayPosition.overlayX, overlayPosition.overlayY);
    return {
      main: overlayPosition,
      fallback: { overlayX: x, overlayY: y },
    };
  }

  /** Adds the configured offset to a position. Follows Material's _addOffset pattern. */
  private _addOffset(position: any): any {
    const offset = this.UNBOUNDED_ANCHOR_GAP;

    if (position.originY === 'top') {
      position.offsetY = -offset;
    } else if (position.originY === 'bottom') {
      position.offsetY = offset;
    } else if (position.originX === 'start') {
      position.offsetX = -offset;
    } else if (position.originX === 'end') {
      position.offsetX = offset;
    }

    return position;
  }

  /** Inverts an overlay position (e.g. start -> end, top -> bottom). Used for fallback positioning. */
  private _invertPosition(
    x: 'start' | 'center' | 'end',
    y: 'top' | 'center' | 'bottom'
  ): { x: 'start' | 'center' | 'end'; y: 'top' | 'center' | 'bottom' } {
    const position = this.position();

    if (
      position === 'top' ||
      position === 'bottom' ||
      position === 'top-start' ||
      position === 'top-end' ||
      position === 'bottom-start' ||
      position === 'bottom-end'
    ) {
      if (y === 'top') {
        y = 'bottom';
      } else if (y === 'bottom') {
        y = 'top';
      }
    } else {
      if (x === 'end') {
        x = 'start';
      } else if (x === 'start') {
        x = 'end';
      }
    }

    return { x, y };
  }

  /**
   * Monitor position changes using positionChanges observable from CDK overlay Similar to Material's approach for
   * tracking position updates
   */
  private monitorPositionChanges(): void {
    if (!this.overlayRef) {
      return;
    }

    const strategy = this.overlayRef.getConfig().positionStrategy as FlexibleConnectedPositionStrategy;

    if (!strategy || !strategy.positionChanges) {
      return;
    }

    strategy.positionChanges.pipe().subscribe((change) => {
      // Detect which position was actually applied based on the connection pair
      const appliedPosition = this.getAppliedPosition(change.connectionPair);

      // Update component with actual applied position
      if (this.componentRef) {
        this.componentRef.setInput('position', appliedPosition);
      }

      // Close tooltip if clipped by viewport
      if (change.scrollableViewProperties.isOverlayClipped && this.isTooltipVisible()) {
        this.hide(0);
      }
    });
  }

  /** Determine applied position from connection pair Maps CDK's ConnectionPositionPair to our TooltipPosition type */
  private getAppliedPosition(connectionPair: any): TooltipPosition {
    const { overlayY, originX, originY } = connectionPair;

    // If overlay is centered on Y axis, it's left or right
    if (overlayY === 'center') {
      return originX === 'start' ? 'left' : 'right';
    }

    // Otherwise it's above or below
    const isAbove = overlayY === 'bottom' && originY === 'top';
    const position = isAbove ? 'top' : 'bottom';

    // Check horizontal alignment to determine if it's start, center, or end
    // For now, return base top/bottom - could be enhanced to detect corners
    return position;
  }

  /** Cleanup resources on directive destroy: Clears all timers, event listeners, overlay, and subjects */
  private cleanup(): void {
    // Clear all pending timers
    if (this.showTimeoutId !== null) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }

    if (this.hideTimeoutId !== null) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }

    // Clear touchstart long-press timeout
    if (this.touchstartTimeout) {
      clearTimeout(this.touchstartTimeout);
      this.touchstartTimeout = null;
    }

    // Unregister all event listeners (Material pattern)
    for (const unlisten of this.eventListeners) {
      unlisten();
    }
    this.eventListeners = [];

    // Dispose overlay and clear component reference
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.componentRef = null;
    }

    // Complete RxJS subject to unsubscribe all observables
    this.destroyed$.next();
    this.destroyed$.complete();

    // Clean ARIA attributes
    this.renderer2.removeAttribute(this.elementRef.nativeElement, 'aria-describedby');
  }
}
